import { BaseSiteService, ConfigInitializerService, OccEndpointsService } from '@spartacus/core';
import { Inject, Injectable, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import { BehaviorSubject, Observable, of, first, map, switchMap, tap } from 'rxjs';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';

import { BrandsEnum } from '@seb/web-design-system/core';

import { BrandsCorner, CustomBaseStore, OneyConfiguration, CssHierarchy, OneySimulation, Key } from '../models';
import { BASESTORE_KEY, GEOLOCATION_KEY, HIERARCHY_KEY } from '../constants/transfer-state-keys';
import { CssHierarchyMapper } from '../mappers/css-hierarchy.mapper';
import { SERVER_BASESTORE } from '../constants/server-tokens';

@Injectable({
  providedIn: 'root'
})
export class BaseStoreService {
  private readonly baseStore$ = new BehaviorSubject<CustomBaseStore>(undefined);
  private oneyConfiguration?: OneyConfiguration[];

  constructor(
    private http: HttpClient,
    private baseSiteService: BaseSiteService,
    private occEndpointsService: OccEndpointsService,
    private configInitializerService: ConfigInitializerService,
    private readonly cssHierarchyMapper: CssHierarchyMapper,
    private readonly transferState: TransferState,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    @Optional() @Inject(SERVER_BASESTORE) private readonly serverBaseStore?: CustomBaseStore
  ) {}

  get baseStore(): CustomBaseStore {
    return this.baseStore$.value;
  }

  getBaseStore(): Observable<CustomBaseStore> {
    return this.baseStore$.asObservable().pipe(
      first((baseStore) => !!baseStore)
    );
  }

  /**
   * Initialize BaseStore :
   * - in ssr mode, add the already retrieved baseStore (see web-server) into the transfer state
   * - in browser mode, get the baseStore saved into the transfer state
   *   but try to update it with fresher values
   * @returns The baseStore observable
   */
  initBaseStore(): Observable<CustomBaseStore> {
    if (isPlatformServer(this.platformId) && this.serverBaseStore) {
      this.transferState.set(BASESTORE_KEY, this.serverBaseStore);
      this.baseStore$.next(this.serverBaseStore);
      return of(this.serverBaseStore);
    }

    let baseStore: CustomBaseStore;

    if (this.transferState.hasKey(BASESTORE_KEY)) {
      baseStore = this.transferState.get(BASESTORE_KEY, undefined);
      this.transferState.remove(BASESTORE_KEY);
    }

    return this.configInitializerService.getStable('backend.occ', 'context').pipe(
      switchMap(() => baseStore ? (
        this.http.get<CustomBaseStore>(this.occEndpointsService.buildUrl('liveBaseStore', {
          urlParams: { baseStoreId: baseStore.uid }
        }))
      ) : (
        this.baseSiteService.get().pipe(
          switchMap((value) => this.http.get<CustomBaseStore>(this.occEndpointsService.buildUrl('baseStore', {
            urlParams: { baseStoreId: (value.baseStore as CustomBaseStore).uid }
          })))
        )
      )),
      tap((value) => this.baseStore$.next(value))
    );
  }

  getGeolocationKey(): Observable<string> {
    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(GEOLOCATION_KEY)) {
      return of(this.transferState.get(GEOLOCATION_KEY, undefined));
    }
    const url = this.occEndpointsService.buildUrl('geolocationKey');
    return this.http.get<Key>(url).pipe(
      map(({ key }) => {
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(GEOLOCATION_KEY, key);
        }
        return key;
      })
    );
  }

  getSisCategories(type: string, selectedCategory?: string, level?: number): Observable<CssHierarchy> {
    const key = `${type}_${selectedCategory}_${level}`;

    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(HIERARCHY_KEY)) {
      const sisCategories = this.transferState.get(HIERARCHY_KEY, undefined)?.[key];
      if (sisCategories !== undefined) {
        this.transferState.remove(HIERARCHY_KEY);
        return of(sisCategories);
      }
    }

    const queryParams: any = { type };
    if (selectedCategory) {
      queryParams.selectedCategory = selectedCategory;
    }
    if (level) {
      queryParams.level = level;
    }

    const url = this.occEndpointsService.buildUrl('cssHierarchy', { queryParams });

    return this.http.get<CssHierarchy>(url).pipe(
      map((hierarchy) => {
        const cssHierarchy = this.cssHierarchyMapper.convertProducts(hierarchy);
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(HIERARCHY_KEY, { [key]: cssHierarchy });
        }
        return cssHierarchy;
      })
    );
  }

  getOneyConfiguration(): Observable<OneyConfiguration[] | undefined> {
    if (!this.baseStore.enableInstallmentPaymentProvider || (this.baseStore.installmentPaymentProvider !== 'ONEY')) {
      return of([]);
    }

    if (this.oneyConfiguration) {
      return of(this.oneyConfiguration);
    }

    return this.http.get<OneyConfiguration[]>(this.occEndpointsService.buildUrl('oneyConfig')).pipe(
      tap((config) => {
        this.oneyConfiguration = config;
      })
    );
  }

  getOneySimulationsProduct(productCode: string): Observable<OneySimulation | undefined> {
    const url = this.occEndpointsService.buildUrl('oneySimulationProduct', {
      urlParams: { productCode }
    });
    return this.http.get<OneySimulation>(url);
  }

  getOneySimulationsBundle(bundleId: string): Observable<OneySimulation | undefined> {
    const url = this.occEndpointsService.buildUrl('oneySimulationBundle', {
      urlParams: { bundleId }
    });
    return this.http.get<OneySimulation>(url);
  }

  getOneySimulationsCart(userId: string, cartId: string): Observable<OneySimulation | undefined> {
    const url: string = this.occEndpointsService.buildUrl('oneySimulationCart', { urlParams: { userId, cartId } });
    return this.http.get<OneySimulation>(url);
  }

  getBrand(brand: BrandsEnum): BrandsCorner | undefined {
    if (this.baseStore?.brandsCorner?.length) {
      return this.baseStore.brandsCorner.find(({ id }) => id === brand);
    }
    return undefined;
  }
}
